<template>
  <div class="profile-container">
    <div class="page-header-container">
      <div class="page-header">
        <span class="page-title">{{ $t("user.profile.title") }}</span>
        <FormActionBtns
          v-model="status"
          :hiddenAdd="true"
          :onCancel="cancel"
          :onSave="save"
          class="action-btns"
        />
      </div>
    </div>
    <div class="profile-main">
      <div class="profile-basic">
        <div class="profile-section-title">
          {{ $t("user.profile.basicInformation") }}
        </div>
        <div class="profile-item">
          <label class="emphasis">{{ $t('user.fullName') }}</label>
          <span class="label-content">{{ userName(currentUser) }}</span>
        </div>
        <div class="profile-item">
          <label class="emphasis">{{ $t('username') }}</label>
          <span class="label-content">{{ currentUser.username }}</span>
        </div>
        <div class="profile-item">
          <label class="emphasis">{{ $t('position') }}</label>
          <span class="label-content">{{ currentUser.title || $t('na') }}</span>
        </div>
        <div class="profile-item">
          <label class="emphasis">{{ $t('userType.name') }}</label>
          <span class="label-content">{{ $t(userType) }}</span>
        </div>
        <div class="profile-item">
          <label class="emphasis">{{ $t('user.phone') }}</label>
          <span class="label-content">{{ primaryContactPhoneNumber || $t('na') }}</span>
        </div>
      </div>
      <div class="profile-account-and-dashboard">
        <div class="profile-account">
          <template v-if="!globalOptionsReady">
            {{ $t('loading') }}
          </template>
          <template v-else>
            <div class="profile-section-title">
              {{ $t("user.profile.accountInformation") }}
            </div>
            <div class="profile-item">
              <label class="emphasis">{{ $t('user.profile.defaultRegion') }}</label>
              <span
                v-if="!isEdit"
                class="label-content"
              >{{ selectedRegion.name }}</span>
              <HKLSelect
                v-else
                :value="selectedRegionId"
                :options="regionHklOptions"
                :showSearch="true"
                placeholder=""
                @change="onRegionChange"
              />
            </div>
            <div class="profile-item">
              <label class="emphasis">{{ $t('user.profile.defaultLanguage') }}</label>
              <div v-if="langOptions.length > 1">
                <span
                  v-if="!isEdit"
                  class="label-content"
                >{{ customLangLabel(selectedLang) }}</span>
                <HKLSelect
                  v-else
                  :value="selectedLangId"
                  :options="langHklOptions"
                  :showSearch="true"
                  placeholder=""
                  @change="onLangChange"
                />
              </div>
              <div v-else-if="langOptions.length === 1">
                {{ customLangLabel(langOptions[0]) }}
              </div>
            </div>
          </template>
        </div>
        <div class="profile-dashboard">
          <div class="profile-section-title">
            {{ $t("user.profile.dashboardSettings") }}
          </div>
          <div class="profile-item">
            <label class="emphasis">{{ $t('user.profile.defaultDashboard') }}</label>
            <span class="label-content">{{ customDashboardLabel(selectedDashboardOption) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import userHelper from '@/utils/userHelper'
import notification from '@/utils/notification'
import * as userUtil from '@/api/users/util'
import { utils as formUtils } from '@/plugin/InputChange'
import { getEngineerProfile, updateEngineerProfile } from '@/api/users/request'
import { userTypeMapping, ENGINEER_DASHBOARDS } from '@/utils/locale'
import FormActionBtns from '@/controls/FormActionBtns'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import {
  globalAuthLogin,
  updateEngineerDefaultLanguage,
  updateEngineerDefaultRegion,
} from '@/api/global/request'
import { useActiveRegionOpts } from '@/store/modules/regions.hook'
import { bShowModConfiguredOption } from '../utils/dashboardUtils'

export default {
  name: 'PageProfile',
  components: {
    FormActionBtns,
    HKLSelect,
  },
  setup () {
    const regionOptions = useActiveRegionOpts()
    return {
      regionOptions,
    }
  },
  data () {
    return {
      pendingUpdateUser: {},
      profileData: {},
      langOptions: [],
      status: 'view',
    }
  },
  computed: {
    ...mapGetters('regions', ['activeRegions', 'enableTenantProjectsFeature', 'enableWorkPermitsFeature', 'enableProjectFeature',
      'enableTenderFeature', 'enableTenancyRecordsFeature', 'enableDirectoryFeature', 'regionLanguageMap']),
    ...mapGetters('user', ['currentUser', 'userRegions', 'currentGlobalUser']),
    ...mapGetters('language', ['regionLanguages', 'currentUserDefaultLang']),
    dashboardOptions () {
      const allDashboardOptions = [
        {
          code: ENGINEER_DASHBOARDS.CMS,
          fullName: this.$t('settings.dashboard.cms'),
        },
        {
          code: ENGINEER_DASHBOARDS.TMS,
          fullName: this.$t('settings.dashboard.tms'),
        },
        {
          code: ENGINEER_DASHBOARDS.WP,
          fullName: this.$t('settings.dashboard.wp'),
        },
      ]
      const enabledDashboardOptions = allDashboardOptions.filter(item => bShowModConfiguredOption.call(this, item.code))
      return enabledDashboardOptions.sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
    userType () {
      return userTypeMapping[this.currentUser.usertype]
    },
    primaryContactPhoneNumber () {
      return (userUtil.getPrimaryPhoneNumberContact(this.currentUser).value) || ''
    },
    globalOptionsReady () {
      return this.regionOptions && this.regionOptions.length && this.langOptions
    },
    selectedRegion () {
      const selectedId = this.pendingUpdateUser.defaultRegion || (
        this.currentGlobalUser && this.currentGlobalUser.defaultRegion && this.currentGlobalUser.defaultRegion.id
      )
      return (this.regionOptions || []).find(opt => opt.id === selectedId) || (this.userRegions || []).find(opt => opt.id === selectedId)
    },
    selectedLang () {
      const selectedId = this.pendingUpdateUser.defaultLanguage || (
        this.currentGlobalUser && this.currentGlobalUser.defaultLanguage && this.currentGlobalUser.defaultLanguage.id
      )
      return (this.langOptions || []).find(opt => opt.id === selectedId) || this.langOptions[0]
    },
    selectedRegionId () {
      const region = this.selectedRegion
      return region ? region.id : undefined
    },
    selectedLangId () {
      const lang = this.selectedLang
      return lang ? lang.id : undefined
    },
    regionHklOptions () {
      return (this.regionOptions || []).map((opt) => ({
        name: opt.displayName || opt.name,
        value: opt.id,
      }))
    },
    langHklOptions () {
      return (this.langOptions || []).map((opt) => ({
        name: this.customLangLabel(opt),
        value: opt.id,
      }))
    },
    selectedDashboardOption () {
      const selectedId = this.pendingUpdateUser.dashboard || (this.currentUser && this.currentUser.dashboard)
      return (this.dashboardOptions || []).find(opt => opt.code === selectedId)
    },
    isEdit () {
      return this.status === 'edit'
    },
  },
  async mounted () {
    await this.initUser()
    await this.initGlobalOptions()
  },
  methods: {
    ...mapActions('user', ['reloadUser', 'updateCurrentGlobalUser']),
    async initUser () {
      const profileData = await getEngineerProfile()
      this.profileData = profileData
      this.pendingUpdateUser = lodash.cloneDeep(profileData)
    },
    async initGlobalOptions () {
      this.langOptions = this.regionLanguages
    },
    cancel () {
      formUtils.resetDirty()
      this.pendingUpdateUser = lodash.cloneDeep(this.profileData)
    },
    async save () {
      formUtils.resetDirty()
      try {
        await this.updateDefaultRegion()
        await this.updateDefaultLanguage()

        const userInfo = await globalAuthLogin()
        this.updateGlobalUser(userInfo)
        await updateEngineerProfile({user: {
          // contacts is for primary email update, follow the logic in HKLUserDetail.vue
          contacts: this.pendingUpdateUser.contacts,
          dashboard: this.pendingUpdateUser.dashboard,
        }})
        this.pendingUpdateUser = lodash.cloneDeep(this.profileData)
        notification.fireNotification(true, this.$i18n.t('success'))
      } catch (error) {
        notification.handleResponseError(error)
      }
    },
    async updateDefaultRegion () {
      this.pendingUpdateUser.defaultRegion && await updateEngineerDefaultRegion(this.pendingUpdateUser.defaultRegion)
      this.profileData.defaultRegion = this.pendingUpdateUser.defaultRegion
    },
    async updateDefaultLanguage () {
      this.pendingUpdateUser.defaultLanguage && await updateEngineerDefaultLanguage(this.pendingUpdateUser.defaultLanguage)
      this.profileData.defaultLanguage = this.pendingUpdateUser.defaultLanguage
    },
    userName (user) {
      if (user.username) {
        const name = userHelper.getUserName(user.username, user.displayName)
        return name
      }
    },
    onRegionChange (regionId) {
      const opt = (this.regionOptions || []).find((o) => o.id === regionId)
      if (opt) {
        this.updateRegion(opt)
      }
    },
    onLangChange (langId) {
      const opt = (this.langOptions || []).find((o) => o.id === langId)
      if (opt) {
        this.updateLang(opt)
      }
    },
    async updateRegion (opt) {
      this.$set(this.pendingUpdateUser, 'defaultRegion', opt.id)
      const regionName = opt.key || opt.name
      const regionLanguages = lodash.get(this.regionLanguageMap, [regionName], [])
      if (regionLanguages.length > 0) {
        this.langOptions = regionLanguages
      } else {
        console.warn(this.$t('error.globalLangNotFound'))
      }
      this.updateLang(this.selectedLang)
    },
    async updateLang (opt) {
      this.$set(this.pendingUpdateUser, 'defaultLanguage', opt.id)
    },
    updateGlobalUser (updatedUser) {
      if (!lodash.isEmpty(updatedUser)) {
        this.updateCurrentGlobalUser(updatedUser)
      }
    },
    customRegionLabel (option) {
      return option.name
    },
    customLangLabel (option) {
      return this.$t(`langOptions.${option.name}`)
    },
    customDashboardLabel (option) {
      return this.$t(`settings.dashboard.${option.code.toLowerCase()}`)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';

.profile-container {
  position: relative;
  background: #FBFAFC;
  .profile-item {
    min-height: 65px;
    label {
      margin-top: 0;
    }
  }
}
.page-header-container {
  color: #0c3a58;
  font-family: Georgia;
  padding: 26px 15px 18px 15px;
  background: white;
  box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
}

.page-header .action-btns {
  float: right;
}

.page-title {
  font-size: 32px;
  color: #0c2b46;
}

.page-header,
.profile-main {
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
}

.profile-main {
  display: flex;
  min-height: 520px;
  margin: 30px auto;
  background: #f8f8f8;
}

.profile-basic,
.profile-account,
.profile-dashboard {
  background: white;
  padding: 24px;
  box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
}
.profile-basic {
  flex: 1;
}
.profile-account-and-dashboard {
  flex: 0.6;
  display: flex;
  flex-direction: column;
  margin-left: 24px;
}
.profile-account {
  flex: 1;
  .profile-item {
    margin-bottom: 20px;
  }
}
.profile-dashboard {
  flex: 0.8;
  margin-top: 24px;
  .profile-dashboard-radios {
    display: flex;
    label {
      flex: auto
    }
  }
}

.profile-section-title {
  font-weight: bold;
  min-height: 40px;
}
</style>
